"use client";

import type { GamePageTranslations } from "@casino/server/graphql/getCmsGamePagePhrases";
import type { FC, PropsWithChildren, RefObject } from "react";
import * as React from "react";
import useAuthGate, { type AuthTab } from "./useAuthGate";
import useCurrencyRefresh from "./useCurrencyRefresh";
import useViewState, { type ViewTypes } from "./useViewState";

type GamePageContextProps = {
  view: ViewTypes;
  isDesktop: boolean;
  isExpanded: boolean;
  toggleExpand: () => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  gameBoxRef: RefObject<HTMLDivElement | null>;
  isAuthModalVisible: boolean;
  setAuthModalVisible: (isVisible: boolean) => void;
  requestAuth: (tab?: AuthTab) => void;
  authTab: AuthTab;
  isLoggedIn: boolean;
  isFunMode: boolean;
  setFunMode: (isFunMode: boolean) => void;
  inGameCurrency?: string;
  setInGameCurrency: (currency?: string) => void;
  inGameCurrencyList: string[];
  walletCurrency?: string;
  isCurrencyChangeEnabled: boolean;
  isRegistrationDisabled: boolean;
  isLoyaltyHidden: boolean;
  t: (key: string, fallback: string) => string;
};

type Props = PropsWithChildren<{
  isLoggedIn?: boolean;
  inGameCurrency?: string;
  inGameCurrencyList?: string[];
  walletCurrency?: string;
  isCurrencyChangeEnabled?: boolean;
  isRegistrationDisabled?: boolean;
  isLoyaltyHidden?: boolean;
  translations?: GamePageTranslations;
}>;

const GamePageContext = React.createContext<GamePageContextProps | null>(null);

export function useGamePageData() {
  const context = React.useContext(GamePageContext);
  if (!context) {
    throw new Error("useGamePageData must be used within a GamePageProvider.");
  }
  return context;
}

export const GamePageDataProvider: FC<Props> = ({
  children,
  isLoggedIn = false,
  inGameCurrency: initialInGameCurrency,
  inGameCurrencyList = [],
  walletCurrency,
  isCurrencyChangeEnabled = false,
  isRegistrationDisabled = false,
  isLoyaltyHidden = false,
  translations = {},
}) => {
  const viewState = useViewState();
  const authGate = useAuthGate(isRegistrationDisabled);
  const [isFunMode, setFunMode] = React.useState(!isLoggedIn);
  const [inGameCurrency, setInGameCurrency] = React.useState(
    initialInGameCurrency,
  );

  useCurrencyRefresh();

  const { requestAuth } = authGate;
  React.useEffect(() => {
    const handleAuthRequest = (event: Event) => {
      const tab = (event as CustomEvent<{ tab?: AuthTab }>).detail?.tab;
      requestAuth(tab);
    };
    window.addEventListener("gamePageAuthRequest", handleAuthRequest);
    return () =>
      window.removeEventListener("gamePageAuthRequest", handleAuthRequest);
  }, [requestAuth]);

  const t = React.useCallback(
    (key: string, fallback: string) => translations[key] ?? fallback,
    [translations],
  );

  const value: GamePageContextProps = {
    ...viewState,
    ...authGate,
    isLoggedIn,
    isFunMode,
    setFunMode,
    inGameCurrency,
    setInGameCurrency,
    inGameCurrencyList,
    walletCurrency,
    isCurrencyChangeEnabled,
    isRegistrationDisabled,
    isLoyaltyHidden,
    t,
  };

  return (
    <GamePageContext.Provider value={value}>
      {children}
    </GamePageContext.Provider>
  );
};
